import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isPlatformAdminRole } from "@/utils/accessControl";
import { cn } from "@/lib/utils";
import { PartnerAttributionSection } from "@/components/pages/guesteria-partners/components/PartnerAttributionSection";
import {
  getPartnerRevenuePresetRange,
  DEFAULT_PARTNER_REVENUE_PRESET,
  type PartnerRevenueDateRange,
} from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";
import { usePartnerOrdersSummary } from "@/components/pages/guesteria-partners/hooks/usePartnerOrdersSummary";
import { formatPartnerRevenueValue } from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFormat";
import { usePartnerAttribution } from "@/components/pages/guesteria-partners/hooks/usePartnerAttribution";
import { useAdminHotelPartnerAttribution } from "@/components/pages/admin-partners/hooks/partner-revenue-summary.hooks";
import { useAdminTenantId } from "../../admin/hooks/useAdminTenantId";
import { copyAnalyticsDateParams } from "../config/analyticsQueryParams";
import { parseServiceCategoryFilter } from "../config/analyticsFdTabs";
import { isRapidPlan, isStarterPlan } from "../config/planAccess";
import { useAnalyticsPage } from "../context/AnalyticsPageContext";
import { useRegisterAnalyticsTabExport } from "../context/AnalyticsTabExportContext";
import { useAnalyticsOverviewRange } from "../hooks/useAnalyticsOverviewRange";
import { useRapidPartnersRevenueKpi } from "../hooks/useRapidPartnersRevenueKpi";
import { useServicesAnalytics } from "../hooks/useServicesAnalytics";
import {
  SERVICES_CONTENT_GAP,
  SERVICES_CONTENT_PADDING,
  SERVICES_INNER_MIN_HEIGHT,
} from "./services/constants";
import { ServicesCategoryPills } from "./services/ServicesCategoryPills";
import { ServicesRapidTotalRevenueCard } from "./services/ServicesRapidTotalRevenueCard";
import { buildRapidExpectedMetric } from "./services/servicesRapidMappers";
import { ServiceUsageDonutCard } from "./services/ServiceUsageDonutCard";
import { TopServicesTable } from "./services/TopServicesTable";
import {
  type ServiceCategoryFilter,
  type TopServiceRow,
} from "./services/mockData";
import {
  buildAnalyticsServiceDetailPath,
  type AnalyticsServiceDetailLocationState,
} from "./services/serviceDetailNavigation";
import {
  mapServicesListToTopRows,
  mapUsageToSegments,
  resolveTopServicesAmountLabel,
} from "./services/servicesTabMappers";
import { downloadServicesTabExport } from "./services/servicesTabExport";
import { AnalyticsExportMenu } from "./shared/AnalyticsExportMenu";

export const ServicesTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rapidCategoryPillOptions = useMemo(
    () => [
      {
        value: "guesteriaPartners" as const,
        label: t(
          "analytics.categories.guesteriaPartners",
          "Guesteria Partners",
        ),
      },
    ],
    [t],
  );
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuthenticatedUser();
  const hasRapidPlan = isRapidPlan(userProfile);
  const hasStarterPlan = isStarterPlan(userProfile);
  const hasPartnersOnlyAnalytics = hasRapidPlan || hasStarterPlan;

  const category: ServiceCategoryFilter = hasPartnersOnlyAnalytics
    ? "guesteriaPartners"
    : parseServiceCategoryFilter(searchParams.get("segment"));
  const deferredCategory = useDeferredValue(category);
  const servicesAnalytics = useServicesAnalytics(dateRange, deferredCategory);
  // DV-84: DV-75 removed the separate partners period selector, so the page
  // date range is the only user-controlled period. Top 5 Services, Service
  // Usage, the revenue KPI and "Activities by room" all use it; the preset
  // default only covers a page range that is not set yet.
  const partnersRange = useMemo<PartnerRevenueDateRange>(
    () =>
      dateRange.from && dateRange.to
        ? { from: dateRange.from, to: dateRange.to }
        : getPartnerRevenuePresetRange(DEFAULT_PARTNER_REVENUE_PRESET),
    [dateRange.from, dateRange.to],
  );
  const rapidRevenueKpi = useRapidPartnersRevenueKpi(partnersRange);
  // DV-119 (owner decision): Expected is shown again, on the same page period
  // as Earned, from the partner orders-summary `pendingEarnings`.
  const { data: partnersOrdersSummary, isLoading: isPartnersSummaryLoading } =
    usePartnerOrdersSummary(partnersRange, undefined, hasRapidPlan);
  const rapidExpectedMetric = useMemo(
    () =>
      buildRapidExpectedMetric(
        partnersOrdersSummary != null
          ? formatPartnerRevenueValue(
              partnersOrdersSummary.pendingEarnings,
              partnersOrdersSummary.currency,
            )
          : "0€",
      ),
    [partnersOrdersSummary],
  );

  // Task 0041 (contract confirmed on dev e4fc7ce5, 2026-09-30) — "Activities
  // by room". Guesteria admin reaches this tab for another hotel via
  // `/analytics/hotels/:hotelId` (the "hotel card"): same UI, the
  // admin-scoped endpoint (needs the hotel's tenantId), everyone else gets
  // the hotel-scoped one.
  const { hotelId: attributionHotelId } = useAnalyticsOverviewRange(dateRange);
  const isPlatformAdmin = isPlatformAdminRole(userProfile);
  const adminTenantId = useAdminTenantId();
  const isGuesteriaPartnersSegment = deferredCategory === "guesteriaPartners";
  const hotelAttribution = usePartnerAttribution(
    attributionHotelId || undefined,
    partnersRange,
    isGuesteriaPartnersSegment && !isPlatformAdmin,
  );
  const adminAttribution = useAdminHotelPartnerAttribution(
    attributionHotelId || undefined,
    adminTenantId,
    partnersRange,
    isGuesteriaPartnersSegment && isPlatformAdmin,
  );
  const attribution = isPlatformAdmin ? adminAttribution : hotelAttribution;

  const isContentPending =
    category !== deferredCategory ||
    servicesAnalytics.isLoading ||
    servicesAnalytics.isFetching;

  useEffect(() => {
    if (!hasPartnersOnlyAnalytics) {
      return;
    }

    const currentTab = searchParams.get("tab");
    const currentSegment = searchParams.get("segment");
    if (currentTab === "services" && currentSegment === "guesteriaPartners") {
      return;
    }

    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set("tab", "services");
      nextParams.set("segment", "guesteriaPartners");
      return nextParams;
    });
  }, [hasPartnersOnlyAnalytics, searchParams, setSearchParams]);

  const filteredTopServices = useMemo(
    () =>
      mapServicesListToTopRows(servicesAnalytics.list.data, deferredCategory),
    [deferredCategory, servicesAnalytics.list.data],
  );

  // The rows belong to deferredCategory, so does the name of the amount column.
  const topServicesAmountLabel = resolveTopServicesAmountLabel(deferredCategory);
  const topServicesAmountHeader = t(
    topServicesAmountLabel.key,
    topServicesAmountLabel.fallback,
  );

  const filteredUsageSegments = useMemo(
    () => mapUsageToSegments(servicesAnalytics.usage.data),
    [servicesAnalytics.usage.data],
  );
  const usageTotal = servicesAnalytics.usage.data?.totalGuests ?? 0;
  const usageServicesCount = servicesAnalytics.usage.data?.totalServices ?? 0;
  const [isExporting, setIsExporting] = useState(false);

  const canExport =
    servicesAnalytics.isReady &&
    !servicesAnalytics.isLoading &&
    !servicesAnalytics.isError;

  const handleExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!canExport) {
        return;
      }

      setIsExporting(true);

      try {
        const didExport = downloadServicesTabExport({
          format,
          hotelId: servicesAnalytics.hotelId,
          from: servicesAnalytics.from,
          to: servicesAnalytics.to,
          topServices: filteredTopServices,
          amountHeader: topServicesAmountHeader,
          usage: servicesAnalytics.usage.data,
          totalGuests: usageTotal,
        });
        if (!didExport) {
          toast.info(
            t(
              "analytics.services.tab.exportNoData",
              "No services analytics data to export",
            ),
          );
          return;
        }

        toast.success(
          t(
            "analytics.services.tab.exportSuccess",
            "Services analytics export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.services.tab.exportError",
            "Failed to export services analytics",
          ),
        );
      } finally {
        setIsExporting(false);
      }
    },
    [
      canExport,
      filteredTopServices,
      servicesAnalytics.from,
      servicesAnalytics.hotelId,
      servicesAnalytics.to,
      servicesAnalytics.usage.data,
      t,
      topServicesAmountHeader,
      usageTotal,
    ],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport: canExport && !isExporting,
      onExport: handleExport,
    }),
    [canExport, handleExport, isExporting],
  );
  useRegisterAnalyticsTabExport(exportRegistration);

  const handleCategoryChange = useCallback(
    (value: ServiceCategoryFilter) => {
      if (hasPartnersOnlyAnalytics) {
        return;
      }

      setSearchParams((previousParams) => {
        const nextParams = new URLSearchParams(previousParams);
        nextParams.set("tab", "services");

        if (value === "all") {
          nextParams.delete("segment");
        } else {
          nextParams.set("segment", value);
        }

        return nextParams;
      });
    },
    [hasPartnersOnlyAnalytics, setSearchParams],
  );

  const handleViewAll = useCallback(() => {
    const nextParams = new URLSearchParams();
    const segment = searchParams.get("segment");
    copyAnalyticsDateParams(searchParams, nextParams);

    if (hasPartnersOnlyAnalytics) {
      nextParams.set("segment", "guesteriaPartners");
    } else if (segment) {
      nextParams.set("segment", segment);
    }

    navigate({
      pathname: "/analytics/services",
      search: nextParams.toString(),
    });
  }, [hasPartnersOnlyAnalytics, navigate, searchParams]);

  const handleServiceClick = useCallback(
    (row: TopServiceRow) => {
      // A Guesteria Partners booking made outside the catalog has no
      // service to navigate to.
      if (row.id == null) {
        return;
      }
      const state: AnalyticsServiceDetailLocationState = { service: row };
      navigate(buildAnalyticsServiceDetailPath(row.id, searchParams), {
        state,
      });
    },
    [navigate, searchParams],
  );

  return (
    <section className="space-y-6 font-sans">
      {hasRapidPlan ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-4">
            <ServicesRapidTotalRevenueCard
              metric={rapidRevenueKpi.metric}
              isLoading={
                rapidRevenueKpi.isLoading || rapidRevenueKpi.isComparisonLoading
              }
            />
            <ServicesRapidTotalRevenueCard
              metric={rapidExpectedMetric}
              loadingMetric={rapidExpectedMetric}
              isLoading={isPartnersSummaryLoading}
            />
          </div>
        </div>
      ) : null}

      {isGuesteriaPartnersSegment ? (
        <PartnerAttributionSection
          report={attribution.data}
          isLoading={attribution.isLoading}
          isError={attribution.isError}
        />
      ) : null}

      <div
        className={cn(
          "flex w-full max-w-full flex-col rounded-2xl border border-[#E5E5E5] bg-white transition-opacity duration-150",
          isContentPending && "opacity-70",
        )}
        style={{
          padding: SERVICES_CONTENT_PADDING,
          gap: SERVICES_CONTENT_GAP,
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ServicesCategoryPills
            value={category}
            onChange={handleCategoryChange}
            options={
              hasPartnersOnlyAnalytics ? rapidCategoryPillOptions : undefined
            }
          />
          <AnalyticsExportMenu
            onExport={handleExport}
            disabled={!canExport || isExporting}
          />
        </div>

        <div
          className="flex w-full max-w-full flex-col items-stretch xl:flex-row"
          style={{
            gap: SERVICES_CONTENT_GAP,
            minHeight: SERVICES_INNER_MIN_HEIGHT,
          }}
        >
          <TopServicesTable
            rows={filteredTopServices}
            segment={deferredCategory}
            className="min-h-0 min-w-0 flex-1"
            onViewAll={handleViewAll}
            onRowClick={handleServiceClick}
          />
          <ServiceUsageDonutCard
            segments={filteredUsageSegments}
            totalServices={usageServicesCount}
            dateRange={dateRange}
          />
        </div>
      </div>
    </section>
  );
};
